import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  AreaChart, 
  Area 
} from "recharts";
import { 
  Users, 
  Calendar as CalendarIcon, 
  BookOpen, 
  Layers, 
  CheckCircle, 
  XCircle, 
  PhoneCall, 
  Trash2, 
  LogOut, 
  Database, 
  Code, 
  Loader2, 
  RefreshCw,
  Search,
  Check,
  Plus,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ["#FF3B30", "#A1A1AA", "#FFFFFF", "#333333"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Navigation State
  const [activeTab, setActiveTab] = useState("overview"); // overview, waitlist, bookings, schema
  
  // Data States
  const [analytics, setAnalytics] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search/Filter States
  const [searchTerm, setSearchTerm] = useState("");

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, waitlistRes, bookingsRes, subsRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/waitlist`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/subscription-inquiries`)
      ]);
      setAnalytics(analyticsRes.data);
      setWaitlist(waitlistRes.data);
      setBookings(bookingsRes.data);
      setSubscriptions(subsRes.data);
    } catch (err) {
      console.error("Failed to load dashboard data", err);
      toast.error("Unauthenticated or error loading data. Please login again.");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update Waitlist entry status
  const updateWaitlistStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/waitlist/${id}`, { status: newStatus });
      toast.success(`Waitlist lead marked as ${newStatus}`);
      fetchData(); // reload
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  // Update Waitlist notes
  const updateWaitlistNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/waitlist/${id}`, { notes });
      toast.success("Notes saved");
      fetchData();
    } catch (err) {
      toast.error("Failed to save notes");
    }
  };

  // Delete Waitlist entry
  const deleteWaitlistEntry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this waitlist entry?")) return;
    try {
      await axios.delete(`${API}/waitlist/${id}`);
      toast.success("Entry deleted successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete entry");
    }
  };

  // Update Booking status
  const updateBookingStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status: newStatus });
      toast.success(`Booking marked as ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update booking");
    }
  };

  // Update Booking notes
  const updateBookingNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { notes });
      toast.success("Booking notes saved");
      fetchData();
    } catch (err) {
      toast.error("Failed to save booking notes");
    }
  };

  // Delete Booking
  const deleteBooking = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`);
      toast.success("Booking deleted successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete booking");
    }
  };

  // Filtered Leads
  const filteredWaitlist = waitlist.filter((entry) => {
    const term = searchTerm.toLowerCase();
    return (
      entry.name.toLowerCase().includes(term) ||
      entry.email.toLowerCase().includes(term) ||
      entry.company.toLowerCase().includes(term) ||
      entry.use_case.toLowerCase().includes(term)
    );
  });

  // Filtered Bookings
  const filteredBookings = bookings.filter((entry) => {
    const term = searchTerm.toLowerCase();
    return (
      entry.name.toLowerCase().includes(term) ||
      entry.email.toLowerCase().includes(term) ||
      entry.company.toLowerCase().includes(term) ||
      entry.use_case.toLowerCase().includes(term)
    );
  });

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center font-mono">
        <Loader2 className="w-10 h-10 animate-spin text-[#FF3B30] mb-4" />
        <span>INITIALIZING COMMAND CONTROL...</span>
      </div>
    );
  }

  // Schema specifications for explorer
  const columnsSpec = [
    { name: "ledger_id", type: "UUID", desc: "Unique hash identifying the sanitized text transaction log." },
    { name: "verified_spend_usd", type: "DECIMAL", desc: "Transaction amount (in USD) confirmed through transaction verification." },
    { name: "duration_minutes", type: "INTEGER", desc: "Duration of booking interval (e.g. 30, 60, 120)." },
    { name: "client_region_hash", type: "STRING (HASH)", desc: "Hashed location of the client to preserve complete privacy." },
    { name: "vetting_duration_secs", type: "INTEGER", desc: "Time delta between first message and verification conclusion." },
    { name: "vetting_result_flag", type: "BOOLEAN", desc: "Whether client successfully passed safety and trust screening." },
    { name: "escort_interpreted_reason", type: "STRING", desc: "Expert escort commentary explaining specific booking context." }
  ];

  const sampleRows = [
    { ledger_id: "c46d-9821-3a21", verified_spend_usd: "350.00", duration_minutes: "60", client_region_hash: "a8f3b1...", vetting_duration_secs: "142", vetting_result_flag: "TRUE", escort_interpreted_reason: "Standard business district booking. Fast verification." },
    { ledger_id: "e90f-3142-2b81", verified_spend_usd: "700.00", duration_minutes: "120", client_region_hash: "f1a2d5...", vetting_duration_secs: "410", vetting_result_flag: "TRUE", escort_interpreted_reason: "Luxury hotel booking. Multiple verification cycles needed." },
    { ledger_id: "b101-2948-4e12", verified_spend_usd: "0.00", duration_minutes: "0", client_region_hash: "d9e831...", vetting_duration_secs: "12", vetting_result_flag: "FALSE", escort_interpreted_reason: "Immediate cancellation. Flagged as bad-faith actor due to mismatched request terminology." },
    { ledger_id: "a394-1102-9f93", verified_spend_usd: "500.00", duration_minutes: "60", client_region_hash: "931df3...", vetting_duration_secs: "185", vetting_result_flag: "TRUE", escort_interpreted_reason: "Late night premium rate. Highly detailed screening passed." }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col md:flex-row font-sans">
      
      {/* Collapsible Left Sidebar */}
      <aside className="w-full md:w-64 bg-[#121212] border-r border-white/10 p-6 flex flex-col justify-between" data-testid="admin-sidebar">
        <div>
          {/* Brand */}
          <div className="flex items-center gap-2 mb-8 pb-6 border-b border-white/10">
            <div className="w-2.5 h-6 bg-[#FF3B30]" />
            <div>
              <div className="font-mono text-xs font-black tracking-widest text-[#FF3B30]">PREMI-SKILLS</div>
              <div className="text-[10px] font-mono text-[#A1A1AA] uppercase tracking-wider">Control Panel</div>
            </div>
          </div>

          {/* User profile brief */}
          <div className="bg-black/40 p-4 border border-white/5 mb-6">
            <div className="text-xs font-mono text-[#A1A1AA]">OPERATOR:</div>
            <div className="text-sm font-bold truncate text-white">{user?.email || "Admin Operator"}</div>
            <span className="inline-block mt-2 px-2 py-0.5 bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] text-[10px] font-mono rounded-none uppercase">SYSTEM CONSOLE</span>
          </div>

          {/* Nav Links */}
          <nav className="space-y-1 font-mono text-xs">
            <button
              onClick={() => { setActiveTab("overview"); setSearchTerm(""); }}
              className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-colors ${
                activeTab === "overview" ? "bg-white text-black font-bold" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}
              data-testid="nav-overview-btn"
            >
              <Layers className="w-4 h-4" /> OVERVIEW DASHBOARD
            </button>
            <button
              onClick={() => { setActiveTab("waitlist"); setSearchTerm(""); }}
              className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-colors ${
                activeTab === "waitlist" ? "bg-white text-black font-bold" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}
              data-testid="nav-waitlist-btn"
            >
              <Users className="w-4 h-4" /> WAITLIST LEADS ({waitlist.length})
            </button>
            <button
              onClick={() => { setActiveTab("bookings"); setSearchTerm(""); }}
              className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-colors ${
                activeTab === "bookings" ? "bg-white text-black font-bold" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}
              data-testid="nav-bookings-btn"
            >
              <CalendarIcon className="w-4 h-4" /> RECONCILIATION BOOKINGS ({bookings.length})
            </button>
            <button
              onClick={() => { setActiveTab("schema"); setSearchTerm(""); }}
              className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-colors ${
                activeTab === "schema" ? "bg-white text-black font-bold" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}
              data-testid="nav-schema-btn"
            >
              <Database className="w-4 h-4" /> DATASET SPEC / SCHEMA
            </button>
            <button
              onClick={() => { setActiveTab("subscriptions"); setSearchTerm(""); }}
              className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-colors ${
                activeTab === "subscriptions" ? "bg-white text-black font-bold" : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
              }`}
              data-testid="nav-subscriptions-btn"
            >
              <CreditCard className="w-4 h-4" /> SUBSCRIPTIONS ({subscriptions.length})
            </button>
          </nav>
        </div>

        {/* Sidebar Footer logout */}
        <div className="pt-6 border-t border-white/10 mt-6 font-mono text-xs">
          <button
            onClick={handleLogout}
            className="w-full py-3 bg-red-950/20 border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-all flex items-center justify-center gap-2 rounded-none"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> TERMINATE SESSION
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 bg-[#050505] p-6 md:p-12 overflow-y-auto relative">
        {/* Decorative Laser Background */}
        <div className="absolute top-0 right-1/3 w-[1px] h-[400px] bg-gradient-to-b from-[#FF3B30] to-transparent opacity-10 pointer-events-none" />

        {/* Dashboard Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 pb-6 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider font-mono text-[#FF3B30]">SYSTEM MODULE // ACTIVE</div>
            <h1 className="text-3xl font-black tracking-tighter uppercase">{activeTab}</h1>
          </div>
          <button 
            onClick={fetchData} 
            className="px-4 py-2 border border-white/10 bg-black/40 hover:bg-white hover:text-black hover:border-white transition-colors text-xs font-mono flex items-center gap-2 rounded-none"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> REFRESH LEDGER
          </button>
        </header>

        {/* 1. OVERVIEW TAB */}
        {activeTab === "overview" && analytics && (
          <div className="space-y-8" data-testid="admin-stats-container">
            {/* North Star Stats Control Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-[#121212] border border-white/10 p-6 rounded-none relative">
                <div className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider">WAITLIST SIGNUPS</div>
                <div className="text-4xl font-black tracking-tight font-mono mt-2 text-white">{analytics.total_waitlist}</div>
                <span className="absolute top-4 right-4 bg-[#FF3B30]/10 p-1 text-[#FF3B30] border border-[#FF3B30]/20">
                  <Users className="w-4 h-4" />
                </span>
              </div>

              <div className="bg-[#121212] border border-white/10 p-6 rounded-none relative">
                <div className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider">SCHEDULED DEMOS</div>
                <div className="text-4xl font-black tracking-tight font-mono mt-2 text-white">{analytics.scheduled_bookings}</div>
                <span className="absolute top-4 right-4 bg-white/5 p-1 text-white border border-white/10">
                  <CalendarIcon className="w-4 h-4" />
                </span>
              </div>

              <div className="bg-[#121212] border border-white/10 p-6 rounded-none relative">
                <div className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider">COMPLETED DEMOS</div>
                <div className="text-4xl font-black tracking-tight font-mono mt-2 text-[#FF3B30]">{analytics.completed_bookings}</div>
                <span className="absolute top-4 right-4 bg-[#FF3B30]/10 p-1 text-[#FF3B30] border border-[#FF3B30]/20">
                  <CheckCircle className="w-4 h-4" />
                </span>
              </div>

              <div className="bg-[#121212] border border-white/10 p-6 rounded-none relative">
                <div className="text-xs font-mono text-[#A1A1AA] uppercase tracking-wider">REMAINING LICENSES</div>
                {/* 5 total licenses. Let's say we have 5 minus approved waitlist items */}
                <div className="text-4xl font-black tracking-tight font-mono mt-2 text-white">
                  {Math.max(0, 5 - waitlist.filter(w => w.status === "approved").length)} / 5
                </div>
                <span className="absolute top-4 right-4 bg-white/5 p-1 text-white border border-white/10">
                  <Layers className="w-4 h-4" />
                </span>
              </div>
            </div>

            {/* Recharts Analytics Bento Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Daily Signups Area Chart */}
              <div className="lg:col-span-8 bg-[#121212] border border-white/10 p-8 rounded-none">
                <div className="mb-6 flex justify-between items-center border-b border-white/10 pb-4">
                  <div>
                    <h3 className="text-sm font-mono uppercase tracking-wider">LEDGER ENGAGEMENT METRICS</h3>
                    <p className="text-[#A1A1AA] text-xs font-mono">Signups and bookings captured over past 7 active calendar periods.</p>
                  </div>
                  <span className="text-[10px] font-mono text-[#FF3B30] uppercase">[7-DAY CHRONOLOGY]</span>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.timeline}>
                      <defs>
                        <linearGradient id="colorWaitlist" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF3B30" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#FF3B30" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FFFFFF" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" stroke="#666" fontSize={10} tickLine={false} />
                      <YAxis stroke="#666" fontSize={10} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "monospace" }} />
                      <Area type="monotone" dataKey="waitlist" stroke="#FF3B30" strokeWidth={2} fillOpacity={1} fill="url(#colorWaitlist)" name="Waitlist Signups" />
                      <Area type="monotone" dataKey="bookings" stroke="#FFFFFF" strokeWidth={2} fillOpacity={1} fill="url(#colorBookings)" name="Booked Demos" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* License Interest Pie Chart */}
              <div className="lg:col-span-4 bg-[#121212] border border-white/10 p-8 rounded-none">
                <div className="mb-6 border-b border-white/10 pb-4">
                  <h3 className="text-sm font-mono uppercase tracking-wider">LICENSE TYPE DEMAND</h3>
                  <p className="text-[#A1A1AA] text-xs font-mono">Distribution of tier preferences.</p>
                </div>
                <div className="h-48 w-full flex items-center justify-center relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Non-Exclusive 1-of-5 License", value: analytics.license_interest || 1 },
                          { name: "Outright Exclusive Buyout", value: analytics.outright_interest || 1 }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        <Cell fill="#FF3B30" />
                        <Cell fill="#FFFFFF" />
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "monospace" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Legend inside */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xs font-mono text-[#A1A1AA]">DEMAND</span>
                    <span className="text-lg font-black font-mono">
                      {(analytics.license_interest + analytics.outright_interest) || 0}
                    </span>
                  </div>
                </div>
                {/* Labels legend */}
                <div className="mt-4 space-y-2 text-xs font-mono">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 bg-[#FF3B30] inline-block" />
                      <span>1-of-5 License:</span>
                    </div>
                    <span>{analytics.license_interest}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 bg-white inline-block" />
                      <span>Outright Buyout:</span>
                    </div>
                    <span>{analytics.outright_interest}</span>
                  </div>
                </div>
              </div>

              {/* Use Case Categories */}
              <div className="lg:col-span-12 bg-[#121212] border border-white/10 p-8 rounded-none">
                <div className="mb-6 border-b border-white/10 pb-4">
                  <h3 className="text-sm font-mono uppercase tracking-wider">USE-CASE FREQUENCY SPLIT</h3>
                  <p className="text-[#A1A1AA] text-xs font-mono">Aggregation of waitlist intended training targets.</p>
                </div>
                <div className="h-44 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.use_cases}>
                      <XAxis dataKey="name" stroke="#666" fontSize={10} tickLine={false} />
                      <YAxis stroke="#666" fontSize={10} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontFamily: "monospace" }} />
                      <Bar dataKey="value" fill="#FF3B30">
                        {analytics.use_cases.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? "#FF3B30" : index === 1 ? "#A1A1AA" : index === 2 ? "#FFFFFF" : "#444444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. WAITLIST LEADS TAB */}
        {activeTab === "waitlist" && (
          <div className="space-y-6">
            {/* Filter Search */}
            <div className="flex bg-[#121212] border border-white/10 px-4 py-3 rounded-none items-center gap-3">
              <Search className="w-5 h-5 text-[#A1A1AA]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search leads by name, email, company, usecase..."
                className="bg-transparent border-none text-white text-sm outline-none w-full font-mono"
              />
            </div>

            {/* Table */}
            <div className="bg-[#121212] border border-white/10 overflow-x-auto rounded-none" data-testid="admin-waitlist-table">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-black/40 font-mono text-xs text-[#A1A1AA]">
                    <th className="p-4">LEAD SPECIFICATION</th>
                    <th className="p-4">INTEREST TIER</th>
                    <th className="p-4">INTENDED INTEL USE CASE</th>
                    <th className="p-4">STATUS</th>
                    <th className="p-4">ADMIN REMARK / NOTES</th>
                    <th className="p-4 text-right">OPERATIONAL ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredWaitlist.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-12 text-center text-[#A1A1AA] font-mono text-xs">
                        [NO WAITLIST LEADS DETECTED]
                      </td>
                    </tr>
                  ) : (
                    filteredWaitlist.map((lead) => (
                      <tr key={lead._id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <div className="font-bold text-white">{lead.name}</div>
                          <div className="text-xs text-[#A1A1AA] font-mono">{lead.email}</div>
                          <div className="text-xs text-[#FF3B30] font-mono uppercase mt-1">{lead.company}</div>
                        </td>
                        <td className="p-4 font-mono text-xs">
                          {lead.interest_tier === "outright" ? (
                            <span className="px-2 py-0.5 bg-white text-black font-bold uppercase">OUTRIGHT BUYOUT</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-red-950/40 text-[#FF3B30] border border-[#FF3B30]/30 uppercase font-bold">1-of-5 LICENSE</span>
                          )}
                        </td>
                        <td className="p-4 max-w-xs text-xs text-[#A1A1AA] truncate">
                          {lead.use_case}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-[10px] font-mono uppercase font-bold ${
                            lead.status === "approved" ? "bg-green-950 text-green-400 border border-green-800" :
                            lead.status === "contacted" ? "bg-blue-950 text-blue-400 border border-blue-800" :
                            lead.status === "rejected" ? "bg-red-950 text-red-400 border border-red-800" :
                            "bg-yellow-950 text-yellow-400 border border-yellow-800"
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="p-4 max-w-xs">
                          <input
                            type="text"
                            defaultValue={lead.notes || ""}
                            placeholder="Add remark..."
                            onBlur={(e) => updateWaitlistNotes(lead._id, e.target.value)}
                            className="bg-black border border-white/10 text-xs py-1.5 px-3 text-white focus:border-[#FF3B30] outline-none font-mono w-full"
                          />
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Action Approve */}
                            <button
                              onClick={() => updateWaitlistStatus(lead._id, "approved")}
                              className="p-1 bg-green-950/20 hover:bg-green-800/50 text-green-400 border border-green-900"
                              title="Mark Approved"
                              data-testid="approve-lead-btn"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            {/* Action Contact */}
                            <button
                              onClick={() => updateWaitlistStatus(lead._id, "contacted")}
                              className="p-1 bg-blue-950/20 hover:bg-blue-800/50 text-blue-400 border border-blue-900"
                              title="Mark Contacted"
                              data-testid="contacted-lead-btn"
                            >
                              <PhoneCall className="w-3.5 h-3.5" />
                            </button>
                            {/* Action Reject */}
                            <button
                              onClick={() => updateWaitlistStatus(lead._id, "rejected")}
                              className="p-1 bg-red-950/20 hover:bg-red-800/50 text-red-400 border border-red-900"
                              title="Mark Rejected"
                              data-testid="reject-lead-btn"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                            {/* Action Delete */}
                            <button
                              onClick={() => deleteWaitlistEntry(lead._id)}
                              className="p-1 bg-zinc-900 hover:bg-red-950/40 text-zinc-500 hover:text-white border border-white/5"
                              title="Delete Entry"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3. BOOKINGS TAB */}
        {activeTab === "bookings" && (
          <div className="space-y-6">
            {/* Filter Search */}
            <div className="flex bg-[#121212] border border-white/10 px-4 py-3 rounded-none items-center gap-3">
              <Search className="w-5 h-5 text-[#A1A1AA]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search bookings by name, email, company, date..."
                className="bg-transparent border-none text-white text-sm outline-none w-full font-mono"
              />
            </div>

            {/* Table */}
            <div className="bg-[#121212] border border-white/10 overflow-x-auto rounded-none" data-testid="admin-bookings-table">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-black/40 font-mono text-xs text-[#A1A1AA]">
                    <th className="p-4">RECONCILIATION DATETIME</th>
                    <th className="p-4">PROSPECT CLIENT</th>
                    <th className="p-4">VETTING QUESTIONS & SCOPE</th>
                    <th className="p-4">STATUS</th>
                    <th className="p-4">INTERNAL RECON NOTES</th>
                    <th className="p-4 text-right">RECON ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-12 text-center text-[#A1A1AA] font-mono text-xs">
                        [NO RECONCILIATION MEETING SCHEDULES FOUND]
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((b) => (
                      <tr key={b._id} className="hover:bg-white/5 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 bg-[#FF3B30] inline-block" />
                            <span className="font-bold text-white font-mono">{b.date}</span>
                          </div>
                          <div className="text-xs text-[#A1A1AA] font-mono mt-1">@ {b.time_slot} UTC</div>
                        </td>
                        <td className="p-4">
                          <div className="font-bold text-white">{b.name}</div>
                          <div className="text-xs text-[#A1A1AA] font-mono">{b.email}</div>
                          <div className="text-xs text-[#FF3B30] font-mono uppercase mt-1">{b.company}</div>
                        </td>
                        <td className="p-4 max-w-xs text-xs text-[#A1A1AA] truncate">
                          {b.use_case}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-[10px] font-mono uppercase font-bold ${
                            b.status === "completed" ? "bg-green-950 text-green-400 border border-green-800" :
                            b.status === "cancelled" ? "bg-red-950 text-red-400 border border-red-800" :
                            "bg-yellow-950 text-yellow-400 border border-yellow-800"
                          }`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="p-4 max-w-xs">
                          <input
                            type="text"
                            defaultValue={b.notes || ""}
                            placeholder="Add memo..."
                            onBlur={(e) => updateBookingNotes(b._id, e.target.value)}
                            className="bg-black border border-white/10 text-xs py-1.5 px-3 text-white focus:border-[#FF3B30] outline-none font-mono w-full"
                          />
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {/* Action Complete */}
                            <button
                              onClick={() => updateBookingStatus(b._id, "completed")}
                              className="p-1 bg-green-950/20 hover:bg-green-800/50 text-green-400 border border-green-900"
                              title="Mark Completed"
                              data-testid="complete-booking-btn"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            {/* Action Cancel */}
                            <button
                              onClick={() => updateBookingStatus(b._id, "cancelled")}
                              className="p-1 bg-red-950/20 hover:bg-red-800/50 text-red-400 border border-red-900"
                              title="Cancel Schedule"
                              data-testid="cancel-booking-btn"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                            {/* Action Delete */}
                            <button
                              onClick={() => deleteBooking(b._id)}
                              className="p-1 bg-zinc-900 hover:bg-red-950/40 text-zinc-500 hover:text-white border border-white/5"
                              title="Delete booking"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 4. SCHEMA TAB */}
        {activeTab === "schema" && (
          <div className="space-y-8">
            <div className="bg-[#121212] border border-white/10 p-8 rounded-none">
              <span className="block text-xs font-mono text-[#FF3B30] uppercase tracking-widest mb-3">[LEDGER METADATA STRUCTURE]</span>
              <h2 className="text-xl font-bold mb-4 tracking-tight">Structured Ledger Spec (CSV / JSON Schema)</h2>
              <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-3xl mb-6">
                The Premi-Skills dataset follows strict table formats. All entries are structured inside sequential log fields. 
                Below are the official schema definitions available for licensed startups and AI training teams.
              </p>

              {/* Schema Spec table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs font-mono">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/40 text-[#A1A1AA]">
                      <th className="p-4">FIELD NAME</th>
                      <th className="p-4">TYPE</th>
                      <th className="p-4">DESCRIPTION / VALIDATION RANGE</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {columnsSpec.map((col) => (
                      <tr key={col.name} className="hover:bg-white/5">
                        <td className="p-4 text-white font-bold">{col.name}</td>
                        <td className="p-4 text-[#FF3B30]">{col.type}</td>
                        <td className="p-4 text-[#A1A1AA]">{col.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Simulated Raw Sample rows */}
            <div className="bg-[#121212] border border-white/10 p-8 rounded-none">
              <span className="block text-xs font-mono text-[#FF3B30] uppercase tracking-widest mb-3">[RESTRICTED PREVIEW // STATIC PRE-DECRYPTED MATRIX]</span>
              <h2 className="text-xl font-bold mb-4 tracking-tight">Raw Simulated Ledger Sample Rows</h2>
              
              <div className="bg-black p-6 border border-white/5 overflow-x-auto">
                <pre className="text-[11px] text-zinc-400 font-mono leading-relaxed">
                  {JSON.stringify(sampleRows, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* 5. SUBSCRIPTIONS TAB */}
        {activeTab === "subscriptions" && (
          <div className="space-y-4" data-testid="subscriptions-panel">
            <p className="text-sm text-[#A1A1AA] font-mono mb-4">
              Subscription checkouts (4 tiers). Custom Agent Skill Development is quote-based and arrives as a lead in the WAITLIST tab (interest_tier: custom_skill_quote) — no automatic charge.
            </p>
            {subscriptions.length === 0 ? (
              <div className="bg-[#121212] border border-white/10 p-8 text-center text-[#A1A1AA] font-mono text-sm">No subscription activity yet.</div>
            ) : (
              subscriptions.map((s) => (
                <div key={s._id} className="bg-[#121212] border border-white/10 p-6" data-testid={`subscription-row-${s._id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                    <div>
                      <div className="font-bold text-white">{s.name} <span className="text-[#A1A1AA] font-normal">· {s.company}</span></div>
                      <div className="text-xs font-mono text-[#A1A1AA]">{s.email}</div>
                    </div>
                    <div className="text-right font-mono text-xs">
                      <span className={`px-2 py-1 border ${s.payment_status === "paid" ? "border-[#30D158]/40 text-[#30D158]" : "border-white/20 text-[#A1A1AA]"}`}>{(s.payment_status || "pending").toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono mb-3">
                    <div><span className="text-[#6b6b6b] block">TIER</span>{(s.tier || "").toUpperCase()}</div>
                    <div><span className="text-[#6b6b6b] block">TERM</span>{s.term === "2yr" ? "2-YEAR" : "1-YEAR"}</div>
                    <div><span className="text-[#6b6b6b] block">AMOUNT/YR</span>A${((s.amount || 0) / 100).toFixed(2)} {(s.currency || "").toUpperCase()}</div>
                    <div><span className="text-[#6b6b6b] block">DATE</span>{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</div>
                  </div>
                  {Array.isArray(s.selected_datasets) && s.selected_datasets.length > 0 && (
                    <div className="text-xs font-mono text-[#A1A1AA] mb-2">DATASETS: {s.selected_datasets.join(", ")}</div>
                  )}
                  {s.tier === "custom" && s.custom_requirements && (
                    <div className="mt-2 bg-black/40 border-l-2 border-[#FF3B30] p-4" data-testid={`custom-request-${s._id}`}>
                      <div className="text-[10px] font-mono text-[#FF3B30] uppercase tracking-widest mb-1">Custom Made request</div>
                      <p className="text-sm text-white leading-relaxed">{s.custom_requirements}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
